import React, { useEffect, useRef, useState } from 'react';
import { createWidget } from '@typeform/embed';
import '@typeform/embed/build/css/widget.css';

interface TypeformEmbedProps {
  formId: string;
  className?: string;
  height?: string;
  minHeight?: string;
  hideHeaders?: boolean;
  hideFooter?: boolean;
  opacity?: number;
  buttonText?: string;
  onSubmit?: () => void;
  onClose?: () => void;
  hiddenFields?: Record<string, string>;
  tracking?: Record<string, string>;
}

const TypeformEmbed: React.FC<TypeformEmbedProps> = ({
  formId,
  className = '',
  height,
  minHeight = '600px',
  hideHeaders = false,
  hideFooter = false,
  opacity = 100,
  onSubmit,
  onClose,
  hiddenFields = {},
  tracking,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (containerRef.current && formId) {
      try {
        // Create the widget
        const { refresh } = createWidget(formId, {
          container: containerRef.current,
          hideHeaders,
          hideFooter,
          opacity: opacity / 100,
          hidden: hiddenFields, // Pass hidden fields to the form
          tracking,
          onSubmit: () => {
            onSubmit?.();
          },
          onClose: () => {
            onClose?.();
          },
          onReady: () => {
            setIsLoading(false);
          },
        });

        // Refresh the widget if the container resizes
        const resizeObserver = new ResizeObserver(() => {
          refresh();
        });

        resizeObserver.observe(containerRef.current);

        return () => {
          if (containerRef.current) {
            resizeObserver.unobserve(containerRef.current);
          }
        };
      } catch (error) {
        console.error('Error loading Typeform:', error);
        setHasError(true);
        setIsLoading(false);
      }
    }
  }, [formId, hideHeaders, hideFooter, opacity, onSubmit, onClose, hiddenFields, tracking]);

  return (
    <div className={`w-full ${className}`}>
      {isLoading && (
        <div className='flex justify-center items-center py-8'>
          <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500'></div>
        </div>
      )}

      {hasError && (
        <div className='bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-center'>
          <p>There was an error loading the form. Please try again later.</p>
          <button
            onClick={() => window.location.reload()}
            className='mt-2 bg-red-100 hover:bg-red-200 text-red-800 font-medium py-2 px-4 rounded transition-colors duration-200'
          >
            Retry
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        className={`w-full transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        style={{
          height: height || '100%',
          minHeight: minHeight,
        }}
      />
    </div>
  );
};

export default TypeformEmbed;
